import { Link, useLocation } from "@tanstack/react-router";
import { useAuth } from "contexts/AuthContext";
import { LogOut, Library, Moon, Store, Sun } from "lucide-react";
import { t } from "i18next";
import { useEffect, useRef, useState } from "react";
import { useTheme } from "next-themes";
import { useLayout } from "@/contexts/LayoutContext";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/button/Button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { useFeatureFlags } from "@/contexts/FeatureFlagContext";
import { cn } from "utils/cn";

export function Navbar() {
  const { authUser, signOut, isLoggedIn } = useAuth();
  const { theme, setTheme } = useTheme();
  const { features } = useFeatureFlags();
  const { setNavbarHeightPx } = useLayout();
  const navbarRef = useRef<HTMLElement>(null);
  const location = useLocation();
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  useEffect(() => {
    const element = navbarRef.current;
    if (!element || typeof ResizeObserver === "undefined") {
      return;
    }
    const updateHeight = () => {
      setNavbarHeightPx(element.offsetHeight);
    };
    updateHeight();
    const resizeObserver = new ResizeObserver(updateHeight);
    resizeObserver.observe(element);
    return () => {
      resizeObserver.disconnect();
      setNavbarHeightPx(0);
    };
  }, [setNavbarHeightPx, features]);

  if (!isLoggedIn) return null;

  const handleSignOut = async () => {
    await signOut();
  };

  const isLibrary = location.pathname === "/library";
  const isCatalog = location.pathname === "/catalog";
  const isDark = mounted && theme === "dark";

  return (
    <header
      ref={navbarRef}
      className="fixed z-50 w-full bg-warm-linen/80 backdrop-blur-md border-b border-secondary-200"
    >
      <div className="flex items-center justify-between min-h-[3.5rem] py-2 px-4 sm:px-6">
        {/* Logo */}
        <Link
          to="/library"
          className="flex items-center gap-2 shrink-0"
        >
          <img
            src="/curioo-text.png"
            alt={t("general:logoText")}
            className="w-auto h-[40px]"
          />
        </Link>

        {/* Center nav tabs */}
        <nav className="hidden sm:flex items-center gap-1 bg-secondary-100/60 rounded-full px-1 py-1">
          <Link
            to="/library"
            className={cn(
              "flex items-center gap-1.5 px-4 py-1.5 rounded-full text-sm font-medium transition-all",
              isLibrary
                ? "bg-card text-card-foreground shadow-sm"
                : "text-secondary-500 hover:text-secondary-700",
            )}
          >
            <Library className="w-3.5 h-3.5" />
            My Library
          </Link>
          <Link
            to="/catalog"
            className={cn(
              "flex items-center gap-1.5 px-4 py-1.5 rounded-full text-sm font-medium transition-all",
              isCatalog
                ? "bg-card text-card-foreground shadow-sm"
                : "text-secondary-500 hover:text-secondary-700",
            )}
          >
            <Store className="w-3.5 h-3.5" />
            Catalog
          </Link>
        </nav>

        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => setTheme(isDark ? "light" : "dark")}
            aria-label={isDark ? "Switch to light mode" : "Switch to dark mode"}
            className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-secondary-200 bg-secondary-100/60 text-secondary-700 transition-colors hover:bg-secondary-100"
          >
            {isDark ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
          </button>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                className="flex items-center gap-2 px-2 border-transparent shadow-none focus:outline-none hover:bg-secondary-100/60"
              >
                <Avatar className="h-8 w-8 border-2 border-secondary-200">
                  <AvatarImage
                    src={authUser?.photoURL ?? undefined}
                    alt={authUser?.displayName ?? ""}
                  />
                  <AvatarFallback className="bg-primary-100 text-primary-700 font-semibold text-xs">
                    {authUser?.email?.[0].toUpperCase() ?? "?"}
                  </AvatarFallback>
                </Avatar>
                <span className="hidden sm:block text-sm font-medium text-secondary-700 max-w-[120px] truncate">
                  {authUser?.displayName || authUser?.email?.split("@")[0]}
                </span>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              sizeToTrigger
              align="start"
              forceMount
            >
              <Button
                onClick={handleSignOut}
                variant="ghost"
                icon={<LogOut className="w-4 h-4" />}
                className="w-full justify-start rounded-md font-normal py-2.5 px-2"
                label={t("general:buttons:logout")}
              />
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {/* Mobile bottom nav */}
      <div className="sm:hidden flex border-t border-secondary-100">
        <Link
          to="/library"
          className={cn(
            "flex-1 flex items-center justify-center gap-1.5 py-2 text-xs font-medium transition-colors",
            isLibrary
              ? "text-primary-600 border-b-2 border-primary-500"
              : "text-secondary-400",
          )}
        >
          <Library className="w-4 h-4" />
          Library
        </Link>
        <Link
          to="/catalog"
          className={cn(
            "flex-1 flex items-center justify-center gap-1.5 py-2 text-xs font-medium transition-colors",
            isCatalog
              ? "text-primary-600 border-b-2 border-primary-500"
              : "text-secondary-400",
          )}
        >
          <Store className="w-4 h-4" />
          Catalog
        </Link>
      </div>

      {features.infoBannerText && (
        <div className="content-around bg-primary px-1 py-2 text-center text-sm text-primary-foreground whitespace-pre-line">
          <p>{features.infoBannerText}</p>
        </div>
      )}
    </header>
  );
}
